import { useSyncExternalStore } from 'react';

// Colour-scheme preference: 'light' / 'dark' / 'auto' (follow the OS).
// The choice is persisted per browser (not per account) and applied by setting
// `data-theme` on <html>, which is what index.css keys its dark palette off.
//
// A module-level store rather than context: the preference is read from a
// couple of unrelated places (settings screen, header) and has to be applied
// before React mounts anyway. public/index.html runs the same resolution
// inline, pre-paint, so there's no flash of the wrong theme on load.

const KEY = 'theme-mode';
export const MODES = ['light', 'dark', 'auto'];

const darkQuery =
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

const readStored = () => {
  try {
    const stored = localStorage.getItem(KEY);
    return MODES.includes(stored) ? stored : 'auto';
  } catch (_) {
    return 'auto'; // storage disabled (private mode / blocked cookies)
  }
};

// The theme actually painted: 'auto' resolves against the OS preference.
export const resolveTheme = (m) => (m === 'auto' ? (darkQuery?.matches ? 'dark' : 'light') : m);

let mode = readStored();
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());

const apply = () => {
  const theme = resolveTheme(mode);
  document.documentElement.dataset.theme = theme;
  // Keep the mobile browser chrome in step with the page.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'dark' ? '#1c100d' : '#E9A319');
};

export function setThemeMode(next) {
  mode = MODES.includes(next) ? next : 'auto';
  try {
    localStorage.setItem(KEY, mode);
  } catch (_) {
    /* preference just won't persist */
  }
  apply();
  emit();
}

// In 'auto', track the OS flipping (e.g. macOS sunset schedule) live.
darkQuery?.addEventListener('change', () => {
  if (mode !== 'auto') return;
  apply();
  emit();
});

apply();

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export default function useTheme() {
  const current = useSyncExternalStore(
    subscribe,
    () => mode,
    () => 'auto'
  );
  return { mode: current, theme: resolveTheme(current), setMode: setThemeMode };
}
